import { PlayRequestStatusesEnum } from 'types/generated/client';
import { useViewer } from 'hooks/useViewer';
import PaperPlane from 'svg/PaperPlane';
import QuoteMarks from 'svg/QuoteMarks';
import type { FindPlayer } from 'screens/Find/types';
import BadgeRating from 'components/BadgeRating';
import Button from 'components/Button';
import RequestStatusBadge from 'components/RequestStatusBadge';
import VerificationBadge from 'components/VerificationBadge';
import classNames from 'styles/utils/classNames';
import { GAME_TYPE_LABEL, REPEAT_SRC, getGameTypeIcon } from './constants';

interface Props {
  player: FindPlayer;
  onViewDetails?: () => void;
  onRequestToPlay?: () => void;
  /** The viewer already asked; the list resolves this once rather than per card. */
  hasPendingRequest?: boolean;
  /** The viewer and this player listed a court or neighborhood in common. */
  playsInSameLocation?: boolean;
  /** From lg up the photo stacks over the details and the icons keep their phone size. */
  isStackedAtDesktop?: boolean;
  /** Stacked at every width, photo leading as a full-width cover — the map popup, which
   *  is one column wide on a phone too. Implies `isStackedAtDesktop`. */
  isPhotoCover?: boolean;
  className?: string;
}

// Top-aligned so the icon stays on the first line when the label wraps.
const DETAIL_ROW =
  'flex items-start gap-ds-sm text-color-text-lightmode-primary dark:text-color-text-darkmode-primary';

/** The "Availability card" shared by the home feed and /find's list + map. */
export default function PlayerCard({
  player,
  onViewDetails,
  onRequestToPlay,
  hasPendingRequest = false,
  playsInSameLocation = false,
  isStackedAtDesktop = false,
  isPhotoCover = false,
  className,
}: Props) {
  const isStacked = isStackedAtDesktop || isPhotoCover;
  const detailRow = classNames(
    DETAIL_ROW,
    isStacked ? 'typography-product-text-card-mobile' : 'typography-product-text-card',
  );
  const detailIcon = classNames('h-4 w-4 shrink-0', !isStacked && 'lg:h-5 lg:w-5');
  const { userId } = useViewer();
  const isOwn = !!player.targetUserId && !!userId && player.targetUserId === userId;
  // The owner of an availability can't request themselves, and one pending ask is the limit.
  const canRequest = !!onRequestToPlay && !isOwn && !hasPendingRequest;
  const gameTypeIcon = getGameTypeIcon(player.competitiveness, detailIcon);

  return (
    <article
      className={classNames(
        'flex flex-col gap-ds-md rounded-[1.25rem] border border-color-border-input-lightmode bg-color-bg-lightmode-primary p-ds-md shadow-[0px_4px_10px_0px_rgba(0,0,0,0.16)] dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary lg:gap-ds-lg lg:p-ds-lg',
        className,
      )}
    >
      <div
        className={classNames(
          'flex items-center gap-ds-sm',
          isPhotoCover ? 'flex-col items-start' : isStackedAtDesktop && 'lg:flex-col lg:items-start',
        )}
      >
        <div className={classNames('relative shrink-0', isPhotoCover && 'w-full')}>
          <img
            src={player.imageUrl}
            alt=""
            className={classNames(
              'rounded-xl border border-color-border-input-lightmode object-cover dark:border-color-border-input-darkmode',
              isPhotoCover
                ? 'h-[9rem] w-full'
                : classNames(
                    'h-[6.5rem] w-[6.5rem]',
                    !isStackedAtDesktop && 'lg:h-[7.5rem] lg:w-[7.5rem]',
                  ),
            )}
          />
          {isOwn && (
            <span className="typography-product-button-label-small absolute left-[0.4375rem] top-[0.4375rem] rounded-xl bg-brand-fire-100 px-1.5 py-0.5 text-brand-fire-600">
              You
            </span>
          )}
        </div>

        <div
          className={classNames(
            'flex min-w-0 flex-1 flex-col gap-ds-sm',
            isPhotoCover ? 'w-full flex-none' : isStackedAtDesktop && 'lg:w-full lg:flex-none',
          )}
        >
          <div className="flex flex-col items-start gap-ds-xs">
            <span className="flex w-full items-center gap-ds-xs">
              <h3 className="typography-product-card-title truncate text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                {player.name}
              </h3>
              <VerificationBadge
                level={player.verificationLevel}
                className={classNames('size-4 shrink-0', !isStacked && 'lg:size-5')}
                inline
              />
              {hasPendingRequest && (
                <RequestStatusBadge status={PlayRequestStatusesEnum.Pending} size="sm" />
              )}
            </span>

            {player.city && (
              <span className="typography-product-text-card w-full truncate text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                {player.city}
              </span>
            )}

            {player.skillLevel && (
              <BadgeRating
                level={player.skillLevel}
                size="xs"
                sizeDesktop={isStacked ? 'xs' : 'sm'}
                variant="neutral"
                sport={player.sport}
              />
            )}
          </div>

          {(player.wantsRegularPartner || gameTypeIcon) && (
            <div className="flex flex-col">
              {player.wantsRegularPartner && (
                <span className={detailRow}>
                  <img
                    src={REPEAT_SRC}
                    alt=""
                    className={classNames(detailIcon, 'object-contain')}
                  />
                  Looking for regular partner
                </span>
              )}
              {gameTypeIcon && (
                <span className={detailRow}>
                  {gameTypeIcon}
                  {GAME_TYPE_LABEL[player.competitiveness]}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {player.description && (
        <div
          className={classNames(
            'flex items-start gap-ds-sm rounded-xl bg-color-bg-lightmode-secondary p-ds-sm dark:bg-color-bg-darkmode-secondary',
            !isStacked && 'lg:p-ds-md',
          )}
        >
          <QuoteMarks
            className={classNames('h-4 w-4 shrink-0', !isStacked && 'lg:h-5 lg:w-5')}
          />
          <p className="typography-product-caption lg:typography-product-text-card-desktop min-w-0 flex-1 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {player.description}
          </p>
        </div>
      )}

      {playsInSameLocation && (
        // Desktop-only: the flag is part of the desktop card redesign.
        <div className="flex items-center gap-ds-sm rounded-xl bg-brand-blue-100 p-ds-md max-lg:hidden">
          <img
            src="/images/app/court-colored.svg"
            alt=""
            className="h-6 w-6 shrink-0 object-contain"
          />
          <p className="typography-product-subheading-mobile min-w-0 flex-1 text-brand-blue-500">
            You play in same location
          </p>
        </div>
      )}

      {(onViewDetails || canRequest) && (
        <div className="mt-auto flex flex-wrap justify-end gap-ds-md">
          {onViewDetails && (
            <Button
              variant="secondary"
              size="sm"
              sizeDesktop={isStacked ? 'sm' : 'md'}
              onClick={onViewDetails}
              className="min-w-0 flex-1 basis-[8.5rem] whitespace-nowrap lg:px-ds-md"
            >
              View details
            </Button>
          )}
          {canRequest && (
            <Button
              variant="brand"
              size="sm"
              sizeDesktop="md"
              onClick={onRequestToPlay}
              className="min-w-0 flex-1 basis-[8.5rem] whitespace-nowrap lg:px-ds-md"
              iconRight={<PaperPlane className="h-3.5 w-3.5 shrink-0 lg:h-4 lg:w-4" />}
            >
              Request to play
            </Button>
          )}
        </div>
      )}
    </article>
  );
}
